import styled from "styled-components";
import { CombinedFieldMode } from "./combinedType";

/**
 * One color per mode (`CombinedFieldMode`) — the single place the five-mode color coding is
 * defined, applied consistently everywhere a mode is shown: the Mode dropdown
 * (`FieldsScreen.tsx`'s `COMBINED_MODE_OPTIONS`, via `ModeDot` baked into each option's `label`),
 * the row header badge (`FieldsScreen.tsx`'s `TypeTag`), and the fields page's intro copy
 * (`strings.tsx`'s `note`).
 * KEEP IN SYNC with `combinedType.ts`'s `COMBINED_FIELD_MODES` and `strings.tsx`'s
 * `modeOptions`/`note` — same five modes. Only touch this when a mode is added, removed, or
 * renamed, or the underlying custom properties change (`SpotterThemeProvider.tsx`) — not a place
 * to casually retune colors for style.
 *
 * Direct/Glossary reuse the app's existing brand tones (amber/purple — already used elsewhere on
 * these screens); Substitute/Format/Conditional get three new tones that bridge between them
 * along the same warm-to-cool family, so all five read as one coherent set rather than an
 * arbitrary rainbow — see `SpotterThemeProvider.tsx`'s own doc comment for the actual hex values.
 */
export const MODE_COLORS: Record<
  CombinedFieldMode,
  { fg: string; bg: string; line: string }
> = {
  direct: {
    fg: "var(--spotterAccent)",
    bg: "var(--spotterAccentSoft)",
    line: "var(--spotterAccentLine)",
  },
  match: {
    fg: "var(--spotterSubstitute)",
    bg: "var(--spotterSubstituteSoft)",
    line: "var(--spotterSubstituteLine)",
  },
  format: {
    fg: "var(--spotterFormat)",
    bg: "var(--spotterFormatSoft)",
    line: "var(--spotterFormatLine)",
  },
  conditional: {
    fg: "var(--spotterConditional)",
    bg: "var(--spotterConditionalSoft)",
    line: "var(--spotterConditionalLine)",
  },
  glossary: {
    fg: "var(--spotterFacial)",
    bg: "var(--spotterFacialSoft)",
    line: "var(--spotterFacialLine)",
  },
};

/** Fixed badge color for the Facial Recognition tab's row badge — every row on that tab is
 *  definitionally Facial Recognition (no mode diversity to color-code), so this is a standalone
 *  constant rather than a `MODE_COLORS` entry. Reuses the same tone Facial Recognition had as a
 *  Fields-tab mode, for visual continuity. */
export const FR_BADGE_COLOR = {
  fg: "var(--spotterMerge)",
  bg: "var(--spotterMergeSoft)",
  line: "var(--spotterMergeLine)",
};

/** Small colored dot rendered next to a mode's name — the fields page's intro copy (`strings.tsx`'s
 *  `note`) uses it directly in running text; `FieldsScreen.tsx`'s `COMBINED_MODE_OPTIONS` bakes it
 *  into each option's `label` instead (a `ReactNode`, per `@cinesys/ui`'s `Select` — no separate
 *  `swatch` prop exists there), so it shows up in both the open menu and the closed control's
 *  selected value automatically. */
export const ModeDot = styled.span<{ $mode: CombinedFieldMode }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 6px;
  border-radius: 50%;
  background: ${(p) => MODE_COLORS[p.$mode].fg};
  vertical-align: middle;
`;
